import classnames from 'classnames'
import { h, Fragment } from 'preact'
import { useEffect, useState } from 'preact/compat'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import { checkPaymentStatus } from '../../../api/payments'
import { PAYMENT_METHOD_TYPES } from '../../../constants/paymentMethods'
import { paymentStatuses } from '../../../constants/paymentStatuses'
import { useTranslations } from '../../../hooks/useTranslations'
import useFailureRedirect from '../../../hooks/useFailureRedirect'
import useAuthoriseError from '../../../hooks/useAuthoriseError'
import {
  formatAsCurrency,
  redirect,
  sprintf,
  getSupportPhoneNumber,
} from '../../../helpers/common'
import InfoMessage from '../../Common/InfoMessage'
import ErrorMessage from '../../Common/ErrorMessage'
import Loader from '../../Common/Loader'
import LinkButton from '../../Common/LinkButton'
import { pageStateSelector } from '../../../redux/slice/pageStateSlice'
import { transactionDetailsSelector } from '../../../redux/slice/transactionDetailsSlice'
import './styles.scss'
import { DataDog } from '../../../datadog/index.js'
import {
  dispatchMFEAdobeEvent,
  dispatchMFEEvent,
} from '../../../helpers/events.js'
import { SUCCESS_RESPONSES } from '../../../constants/serverResponses.js'
import { RESPONSE_ERROR_TEXT } from '../../../helpers/serverTextResponses.js'
import { walleyAuthentication } from '../../../api/walley.js'

const Walley = ({
  handleSubmit,
  showPaymentConfirmation,
  isGoBackButtonVisible,
  loaderImage,
  errors,
}) => {
  const l10n = useTranslations()
  const transactionDetails = useSelector(transactionDetailsSelector)
  const [error, setError] = useState('')
  const [isPaymentPending, setIsPaymentPending] = useState(false)
  const pageState = useSelector(pageStateSelector)
  const { isPaymentBlocked } = pageState
  const [isPaymentDisabled, setPaymentDisabled] = useState(true)
  const [isPaymentStatusAuthorised, setPaymentStatusAuthorised] =
    useState(false)
  const [isPaymentStatusPending, setIsPaymentStatusPending] = useState(true)
  const payButtonClasses = classnames('button', 'primary', 'medium', {
    loading: isPaymentStatusPending,
  })

  useEffect(() => {
    if (!pageState.isPaymentBlocked) {
      setError('')
    }
  }, [pageState.isPaymentBlocked])

  const handlePaymentStatus = () => {
    setIsPaymentStatusPending(true)

    checkPaymentStatus('walley', transactionDetails.id).then(
      ({ responseData }) => {
        if (responseData?.status === paymentStatuses.AUTHORISE_REQUESTED) {
          setPaymentDisabled(true)
          setPaymentStatusAuthorised(true)
        } else {
          setPaymentDisabled(false)
          setPaymentStatusAuthorised(false)
        }

        setIsPaymentStatusPending(false)
      },
    )
  }

  useEffect(async () => {
    window.onpageshow = async event => {
      if (event.persisted) {
        await handlePaymentStatus()
      }
    }

    await handlePaymentStatus()
  }, [])

  useFailureRedirect()
  const handleAuthoriseError = useAuthoriseError()

  const checkIsTermsAndConditionsEnabled = () => {
    if (isPaymentBlocked) {
      setError(l10n.t('payment-btn-error'))
      return false
    }
    setError('')
    return true
  }

  const formatAmount = formatAsCurrency(
    transactionDetails.currency,
    transactionDetails.culture,
  )

  const handlePayment = async () => {
    DataDog.info('DirectDebit payment button clicked')
    dispatchMFEEvent('tui-button.payment-btn-clicked', {
      paymentMethod: 'DirectDebit',
    })
    dispatchMFEAdobeEvent('Pay-DirectDebit')

    if (isPaymentBlocked) {
      setError(l10n.t('payment-btn-error'))
    }

    handleSubmit(async ({ notes, walleyEmail, walleyMobileNumber }) => {
      if (!checkIsTermsAndConditionsEnabled()) {
        return
      }

      const continuePayment = async () => {
        setIsPaymentPending(true)
        const { responseData, responseStatus } = await walleyAuthentication({
          phoneNumber: walleyMobileNumber?.includes('+')
            ? walleyMobileNumber
            : `+${walleyMobileNumber}`,
          email: walleyEmail,
          paymentUrl: window.location.href,
          notes,
        })

        if (responseStatus === SUCCESS_RESPONSES.OK) {
          window.tuiPaymentsMFEInstance?.onAuthorised?.()
          setIsPaymentPending(false)
          redirect(responseData.paymentUrl)
          return
        }

        handleAuthoriseError(responseStatus, responseData)

        if (responseData?.isRetryable) {
          setError(RESPONSE_ERROR_TEXT(l10n, responseStatus))
        }

        setIsPaymentPending(false)
      }

      const mfeElement = window.tuiPaymentsMFEInstance

      if (mfeElement?.onAuthorising) {
        DataDog.info(`onAuthorising hook has been enabled`)

        mfeElement
          .onAuthorising?.({ paymentMethod: PAYMENT_METHOD_TYPES.Walley })
          .then(async () => {
            await continuePayment()
            DataDog.info(`onAuthorising Promise resolved`)
          })
          .catch(() => {
            DataDog.info(`onAuthorising Promise rejected`)
            console.log('onAuthorising Promise rejected')
          })
      } else {
        await continuePayment()
      }
    })()
  }

  const currentAmount = formatAmount(
    transactionDetails?.overrideAmount
      ? transactionDetails.overrideAmount
      : transactionDetails.amount,
  )

  return (
    <>
      <div className="walley-wrapper">
        <ErrorMessage
          isShown={!!errors?.walleyEmail || !!errors?.walleyMobileNumber}
          message={l10n.t('FIELDS_ERROR')}
          data-testid="payment-error"
        ></ErrorMessage>
        {showPaymentConfirmation && (
          <InfoMessage
            message={sprintf(
              l10n.t(`You're about to pay %s with Walley`),
              currentAmount,
            )}
            data-testid="walley-info-container"
            className="walley-info-message"
          />
        )}
        <ErrorMessage
          isShown={!!error}
          message={error}
          data-testid="walley-error"
        />

        <ErrorMessage
          isShown={isPaymentStatusAuthorised}
          message={l10n.t(
            'Your payment request is currently being processed, if you are not redirected, please contact customer services on',
          )}
        >
          <div className="support-phone-number">
            {getSupportPhoneNumber(transactionDetails.culture)}
          </div>
        </ErrorMessage>

        {isPaymentPending ? (
          <Loader loaderImage={loaderImage} />
        ) : (
          <div className="walley-button">
            {isGoBackButtonVisible && (
              <LinkButton
                data-testid="go-back-button"
                title={l10n.t('Go Back')}
                onClick={() => redirect(transactionDetails.returnUrl)}
                className="go-back-button"
              />
            )}
            <button
              type="button"
              className={payButtonClasses}
              data-testid="walley-button"
              onClick={handlePayment}
              disabled={isPaymentDisabled}
            >
              {`${l10n.t('Pay With')} ${l10n.t('Walley')}`}
            </button>
          </div>
        )}
      </div>
    </>
  )
}

Walley.propTypes = {
  handleSubmit: PropTypes.func,
  showPaymentConfirmation: PropTypes.bool,
  isGoBackButtonVisible: PropTypes.bool,
  loaderImage: PropTypes.string,
}

export default Walley
